import React, { FC, ReactNode } from 'react';

import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';

import {
    ChannelDefinition,
    ContentFormat,
    FieldSpec,
} from '../../models/channelDefinition.model';

interface ChannelDefinitionViewProps {
    definition: ChannelDefinition;
    content: Record<string, unknown> | undefined;
    notice?: ReactNode;
    className?: string;
}

const EMPTY_VALUE_PLACEHOLDER = '—';

const formatLeafValue = (
    field: FieldSpec,
    value: unknown,
    contentFormat: ContentFormat,
): string => {
    if (value === undefined || value === null || value === '') {
        return EMPTY_VALUE_PLACEHOLDER;
    }
    if (
        contentFormat === ContentFormat.externalReference &&
        field.supports_alternates &&
        field.type === 'string'
    ) {
        return `External content: ${value}`;
    }
    if (field.type === 'bool') {
        return value ? 'Yes' : 'No';
    }
    return String(value);
};

const LeafValue: FC<{
    field: FieldSpec;
    value: unknown;
    contentFormat: ContentFormat;
}> = ({ field, value, contentFormat }) => {
    if (Array.isArray(value)) {
        if (value.length === 0) {
            return <FieldValue>{EMPTY_VALUE_PLACEHOLDER}</FieldValue>;
        }
        return (
            <AlternatesList>
                {value.map((item, index) => (
                    <li key={index}>
                        {formatLeafValue(field, item, contentFormat)}
                    </li>
                ))}
            </AlternatesList>
        );
    }
    return (
        <FieldValue>{formatLeafValue(field, value, contentFormat)}</FieldValue>
    );
};

const renderFields = (
    fields: FieldSpec[],
    values: Record<string, unknown> | undefined,
    contentFormat: ContentFormat,
    depth: number,
): ReactNode =>
    fields.map((field) => {
        const value = values?.[field.key];

        if (field.type === 'object') {
            const nestedValues =
                value && typeof value === 'object' && !Array.isArray(value)
                    ? (value as Record<string, unknown>)
                    : undefined;
            return (
                <FieldGroup
                    key={field.key}
                    depth={depth}
                    data-testid={`channel-definition-field-${field.key}`}
                >
                    <FieldLabel>{field.key}</FieldLabel>
                    {renderFields(
                        field.fields ?? [],
                        nestedValues,
                        contentFormat,
                        depth + 1,
                    )}
                </FieldGroup>
            );
        }

        return (
            <FieldRow
                key={field.key}
                depth={depth}
                data-testid={`channel-definition-field-${field.key}`}
            >
                <FieldLabel>{field.key}</FieldLabel>
                <LeafValue
                    field={field}
                    value={value}
                    contentFormat={contentFormat}
                />
            </FieldRow>
        );
    });

const ChannelDefinitionView: FC<ChannelDefinitionViewProps> = ({
    definition,
    content,
    notice,
    className,
}) => {
    return (
        <Container className={className}>
            {notice && <Notice>{notice}</Notice>}
            {renderFields(
                definition.fields ?? [],
                content,
                definition.content_format,
                0,
            )}
        </Container>
    );
};

const Container = styled.div({
    width: '100%',
    borderRadius: aampeTheme.borderRadius.default,
    backgroundColor: aampeTheme.colors.backgroundWG02,
    padding: aampeTheme.spacing4(6),
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(4),
});

const Notice = styled.div({
    color: aampeTheme.colors.textAccessibleWG01,
    backgroundColor: aampeTheme.colors.warningDisabled,
    borderRadius: aampeTheme.borderRadius.default,
    padding: aampeTheme.spacing4(3),
    fontSize: '14px',
});

const FieldRow = styled.div<{ depth: number }>(({ depth }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(1),
    marginLeft: aampeTheme.spacing4(depth * 4),
}));

const FieldGroup = styled.div<{ depth: number }>(({ depth }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
    marginLeft: aampeTheme.spacing4(depth * 4),
    borderLeft:
        depth > 0 ? `1px solid ${aampeTheme.colors.bordersWG01}` : 'none',
    paddingLeft: depth > 0 ? aampeTheme.spacing4(3) : '0',
}));

const FieldLabel = styled.span({
    color: aampeTheme.colors.textAccessibleWG01,
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
});

const FieldValue = styled.span({
    color: aampeTheme.colors.textAccessibleWG02,
    fontSize: '14px',
});

const AlternatesList = styled.ul({
    margin: 0,
    paddingLeft: aampeTheme.spacing4(5),
    color: aampeTheme.colors.textAccessibleWG02,
    fontSize: '14px',
});

export default ChannelDefinitionView;
